/**
 * 今日の県のランキング（上位20人と自分の順位）。
 */

import {
  fetchDailyBoard,
  formatDailyRank,
  type RankingIdentityStore,
} from '../meta/dailyRanking';
import { NAME_ADJECTIVES, NAME_NOUNS, splitDailyName } from '../meta/dailyNames';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class RankingModal {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private target: { date: string; region: string; regionName: string; shortDate: string } | null = null;

  constructor(private readonly ids: RankingIdentityStore) {
    this.root = el('div', 'modal ranking-modal');
    this.root.dataset.visible = 'false';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    const backdrop = el('div', 'modal__backdrop');
    backdrop.addEventListener('click', () => this.hide());
    const card = el('div', 'modal__card');
    const head = el('div', 'modal__head');
    this.titleEl = el('h2', 'modal__title', '今日のランキング');
    this.titleEl.id = 'ranking-title';
    this.root.setAttribute('aria-labelledby', 'ranking-title');
    this.closeBtn = el('button', 'btn btn--icon');
    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', '閉じる');
    this.closeBtn.append(el('span', undefined, '✕'));
    this.closeBtn.addEventListener('click', () => this.hide());
    head.append(this.titleEl, this.closeBtn);
    this.body = el('div', 'modal__body');
    card.append(head, this.body);
    this.root.append(backdrop, card);
    document.body.append(this.root);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  async open(target: { date: string; region: string; regionName: string; shortDate: string }): Promise<void> {
    this.target = target;
    this.titleEl.textContent = `今日の県 ${target.shortDate}｜${target.regionName}`;
    this.body.replaceChildren(el('p', 'modal__note', '読み込み中…'));
    this.root.dataset.visible = 'true';
    this.closeBtn.focus();
    await this.load();
  }

  /**
   * 中身を読み直す。`renamed` のときは「呼び名を変えました」を読み上げ、フォーカスを「呼び名をえらぶ」へ戻す
   * （読み直すとボタンが作り直され、フォーカスが body に落ちていた。2026-10-05）。
   */
  private async load(renamed = false): Promise<void> {
    const t = this.target;
    if (!t) return;
    const identity = this.ids.identity;
    const board = await fetchDailyBoard(identity, t.date, t.region);
    const nodes: HTMLElement[] = [];
    if (board.status !== 'ok') {
      nodes.push(el('p', 'ranking-empty', 'ランキングを読み込めませんでした。通信のよいところでもう一度開いてください。自分の記録はこの端末に残っています。'));
    } else {
      nodes.push(
        el(
          'p',
          'ranking-summary',
          board.me
            ? `あなた（${board.me.name}）は ${formatDailyRank(board.me.rank, board.total)} ・ ${board.me.score.toLocaleString('ja-JP')}点`
            : `参加 ${board.total}人。今日の県を遊ぶと載ります（コンティニュー・おはしなしのプレイだけ）。`,
        ),
      );
      const list = el('ol', 'ranking-list');
      board.top.forEach((row, i) => {
        const li = el('li', 'ranking-row');
        if (board.me && row.name === board.me.name && row.score === board.me.score) li.dataset.me = 'true';
        li.append(
          el('span', 'ranking-row__rank', String(i + 1)),
          el('span', 'ranking-row__name', row.name),
          el('span', 'ranking-row__score', `${row.score.toLocaleString('ja-JP')}点`),
        );
        list.append(li);
      });
      if (board.top.length === 0) nodes.push(el('p', 'ranking-empty', 'まだ誰もいません。一番乗りのチャンス。'));
      else nodes.push(list);
    }
    const nameRow = el('div', 'ranking-name');
    // 呼び名を変えたときの知らせ。先に空で置いておき、あとから文字を入れる（読み上げに気づかせるため）
    const status = el('p', 'ranking-status');
    status.setAttribute('role', 'status');
    nameRow.append(el('span', undefined, `あなたの呼び名：${identity.name}`));
    const choose = el('button', 'btn', '呼び名をえらぶ');
    choose.type = 'button';
    choose.setAttribute('aria-expanded', 'false');
    nameRow.append(choose);
    const picker = this.buildNamePicker();
    picker.hidden = true;
    choose.addEventListener('click', () => {
      picker.hidden = !picker.hidden;
      choose.setAttribute('aria-expanded', String(!picker.hidden));
      if (!picker.hidden) picker.querySelector<HTMLSelectElement>('select')?.focus();
    });
    nodes.push(
      nameRow,
      status,
      picker,
      el(
        'p',
        'modal__note ranking-note',
        '呼び名は決まった言葉の組み合わせから選びます（番号は自動）。名前やメールなどは送りません。ランキングの表の呼び名は、次に記録したときに新しくなります。',
      ),
    );
    this.body.replaceChildren(...nodes);
    if (renamed && this.isOpen()) {
      choose.focus({ preventScroll: true });
      window.setTimeout(() => {
        // 「あなたの呼び名」はすぐ変わる。表（サーバー）の呼び名は、次に記録を送ったときに変わる
        status.textContent = '呼び名を変えました。ランキングの表には、次に記録したときに出ます。';
      }, 50);
    }
  }

  /** 前半・後半の言葉を一覧から選ぶ欄。自由入力はさせない（荒らし・個人情報の書き込みを防ぐ）。 */
  private buildNamePicker(): HTMLElement {
    const box = el('div', 'ranking-namepick');
    const current = splitDailyName(this.ids.identity.name);
    const makeSelect = (label: string, words: readonly string[], selected?: string) => {
      const wrap = el('label', 'ranking-namepick__row');
      const select = el('select');
      for (const word of words) {
        const option = el('option', undefined, word);
        option.value = word;
        if (word === selected) option.selected = true;
        select.append(option);
      }
      wrap.append(el('span', undefined, label), select);
      return { wrap, select };
    };
    const first = makeSelect('前半', NAME_ADJECTIVES, current?.adjective);
    const second = makeSelect('後半', NAME_NOUNS, current?.noun);
    const preview = el('span', 'ranking-namepick__preview');
    const number = current?.number ?? '****';
    const sync = () => {
      preview.textContent = `${first.select.value}${second.select.value}#${number}`;
    };
    first.select.addEventListener('change', sync);
    second.select.addEventListener('change', sync);
    sync();

    const actions = el('div', 'ranking-namepick__row');
    const save = el('button', 'btn btn--primary', 'この呼び名にする');
    save.type = 'button';
    save.addEventListener('click', () => {
      if (this.ids.chooseName(first.select.value, second.select.value)) void this.load(true);
    });
    const random = el('button', 'btn', 'おまかせ');
    random.type = 'button';
    random.addEventListener('click', () => {
      this.ids.rerollName();
      void this.load(true);
    });
    actions.append(el('span', undefined, 'できあがり：'), preview, save, random);
    box.append(first.wrap, second.wrap, actions);
    return box;
  }

  hide(): void {
    this.root.dataset.visible = 'false';
  }

  isOpen(): boolean {
    return this.root.dataset.visible === 'true';
  }
}
